/**
 * AliExpress Products Component
 *
 * Displays hot/trending products from AliExpress
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ShoppingBag, ExternalLink, TrendingUp, ChevronRight } from 'lucide-react';
import type { AliExpressHotProduct } from '../../types';

export function AliExpressProducts() {
  // Use React Query for caching (reduces API calls)
  const { data: products = [], isLoading: loading, error } = useQuery<AliExpressHotProduct[]>({
    queryKey: ['aliexpress-hot-products'],
    queryFn: async () => {
      const response = await fetch('/api/aliexpress/hot?pageSize=16');
      if (!response.ok) throw new Error('Failed to load products');
      const data = await response.json();
      return data.products || [];
    },
    staleTime: 1000 * 60 * 30, // 30 minutes
  });

  if (loading) {
    return (
      <section className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ShoppingBag className="text-orange-500" size={24} />
            <h2 className="text-xl font-bold text-gray-900">알리익스프레스 인기 제품</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
              <div className="aspect-square bg-gray-200 rounded-lg mb-3"></div>
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (error || products.length === 0) {
    return null;
  }

  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShoppingBag className="text-orange-500" size={24} />
          <h2 className="text-xl font-bold text-gray-900">알리익스프레스 인기 제품</h2>
          <span className="bg-orange-100 text-orange-600 text-xs font-medium px-2 py-1 rounded-full">
            특가
          </span>
        </div>
        <Link
          to="/products?source=aliexpress"
          className="text-orange-600 hover:text-orange-700 text-sm font-medium flex items-center gap-1"
        >
          더 보기 <ExternalLink size={14} />
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {products.map((product) => (
          <AliExpressProductCard key={product.productId} product={product} />
        ))}
      </div>
    </section>
  );
}

interface ProductCardProps {
  product: AliExpressHotProduct;
}

function AliExpressProductCard({ product }: ProductCardProps) {
  // API returns both USD and KRW prices
  const krwPrice = product.salePrice;
  const originalKrwPrice = product.originalPrice > product.salePrice ? product.originalPrice : null;
  const usdPrice = (product as any).usdSalePrice || Math.round(product.salePrice / 1400 * 100) / 100;
  const usdOriginalPrice = (product as any).usdOriginalPrice;

  const handleClick = () => {
    // Track affiliate click
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'aliexpress_click', {
        product_id: product.productId,
        product_name: product.productTitle,
      });
    }
  };

  // Placeholder SVG data URI - no external dependency
  const placeholderImage = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="300" height="300" viewBox="0 0 300 300"><rect fill="#f3f4f6" width="300" height="300"/><text fill="#9ca3af" font-family="Arial" font-size="14" x="50%" y="50%" text-anchor="middle" dominant-baseline="middle">Product</text></svg>')}`;

  // Use placeholder image if no image available
  const imageUrl = product.imageUrl || placeholderImage;

  return (
    <Link
      to={`/products/aliexpress/${product.productId}`}
      state={{ product }}
      onClick={handleClick}
      className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg hover:border-orange-300 transition-all group block"
    >
      {/* Image */}
      <div className="aspect-square bg-gray-100 relative overflow-hidden">
        <img
          src={imageUrl}
          alt={product.productTitle}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
          loading="lazy"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            if (!target.src.startsWith('data:image')) {
              target.src = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="300" height="300" viewBox="0 0 300 300"><rect fill="#f3f4f6" width="300" height="300"/><text fill="#9ca3af" font-family="Arial" font-size="14" x="50%" y="50%" text-anchor="middle" dominant-baseline="middle">Product</text></svg>')}`;
            }
          }}
        />
        {/* Discount badge */}
        {originalKrwPrice && (
          <div className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
            {Math.round((1 - product.salePrice / product.originalPrice) * 100)}% OFF
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-3">
        <h3 className="text-sm font-medium text-gray-900 line-clamp-2 mb-2 min-h-[40px]">
          {product.productTitle}
        </h3>

        <div className="flex items-center gap-1 mb-2">
          <TrendingUp size={12} className="text-green-500" />
          <span className="text-xs text-gray-500">
            {product.volume.toLocaleString()}개 판매
          </span>
        </div>

        {/* KRW Price */}
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-bold text-orange-600">
            ₩{krwPrice.toLocaleString()}
          </span>
          {originalKrwPrice && (
            <span className="text-xs text-gray-400 line-through">
              ₩{originalKrwPrice.toLocaleString()}
            </span>
          )}
        </div>

        {/* USD Price */}
        <div className="text-xs text-gray-500 mt-1">
          ${usdPrice.toFixed(2)} USD
          {usdOriginalPrice && usdOriginalPrice > usdPrice && (
            <span className="line-through ml-1">${usdOriginalPrice.toFixed(2)}</span>
          )}
        </div>

        <div className="mt-2 flex items-center justify-between">
          <span className="text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded">
            알리익스프레스
          </span>
          <ChevronRight size={14} className="text-gray-400 group-hover:text-orange-500 transition-colors" />
        </div>
      </div>
    </Link>
  );
}

export default AliExpressProducts;
